import { LocationQuery } from 'vue-router';
import { http } from '../../helpers/http.helper';
import { Filters } from '../filters/filter.interface';
import { ApiResource } from './../../models/api-resource.interface';
import { Candidate, CandidateCheckDelete, Poke } from './candidate.interface';

class CANDIDATE_INCLUDES_CLASS {
   all: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'addresses',
         'addresses.addressType',
         'addresses.city',
         'approvals',
         'birthCountry',
         'candidateActivationState',
         'candidatePools.employmentTerminationReason',
         'candidatePools.pool.board',
         'candidatePools.pool.team',
         'candidatePools.pool.tenant',
         'candidatePoolsTrashed.employmentTerminationReason',
         'candidatePoolsTrashed.pool.board',
         'candidatePoolsTrashed.pool.team',
         'candidatePoolsTrashed.pool.tenant',
         'communicationLanguage',
         'communicationPreferences.communicationPreferenceType',
         'courses',
         'dependants',
         'dependants.dependantType',
         'gender',
         'jobDistances',
         'jobReferences',
         'latestApproval',
         'maritalStatus',
         'nationalityCountry',
         'partnerProfession',
         'phoneNumbers',
         'pokes',
         'profile',
         'profile.competences',
         'profile.driversLicenses',
         'profile.educations',
         'profile.jobFunctions',
         'profile.jobFunctions.jobFunctionCategory',
         'profile.jobFunctions.jobFunctionGroup',
         'profile.jobTypes',
         'profile.languages',
         'profile.statutes',
         'profilePicture',
         'recentPoke',
         'recentPoke.pokeType',
         'resume',
         'salutation',
         'socialSecurityNumbers',
         'socialSecurityNumbers.country',
         'status',
         'statuses.candidateStatusType',
         'submissions.journeyReferral',
         'tenant',
         'user',
         'videocallMeetings',
         'workExperiences',
         'workingDays',
      ],
      appends: ['locale', 'scopes', 'activation_state'],
   };
   list: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'statuses.candidateStatusType',
         'addresses',
         'addresses.city',
         'addresses.addressType',
         'communicationLanguage',
         'communicationPreferences.communicationPreferenceType',
         'profile',
         'profile.educations',
         'workExperiences',
         'pokes',
         'recentPoke',
         'recentPoke.pokeType',
         'submissions.journeyReferral',
      ],
      appends: ['locale', 'scopes', 'activation_state'],
   };
   dropdown: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'addresses',
         'addresses.addressType',
         'addresses.city',
         'communicationPreferences.communicationPreferenceType',
         'communicationLanguage',
      ],
      appends: ['locale', 'scopes', 'activation_state'],
   };
   journey: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'addresses',
         'addresses.addressType',
         'addresses.city',
         'approvals',
         'birthCountry',
         'candidateActivationState',
         'candidatePools.employmentTerminationReason',
         'candidatePools.pool.board',
         'candidatePools.pool.team',
         'candidatePools.pool.tenant',
         'candidatePoolsTrashed.employmentTerminationReason',
         'candidatePoolsTrashed.pool.board',
         'candidatePoolsTrashed.pool.team',
         'candidatePoolsTrashed.pool.tenant',
         'communicationLanguage',
         'communicationPreferences.communicationPreferenceType',
         'courses',
         'dependants',
         'dependants.dependantType',
         'gender',
         'jobDistances',
         'jobReferences',
         'latestApproval',
         'maritalStatus',
         'nationalityCountry',
         'partnerProfession',
         'phoneNumbers',
         'pokes',
         'profile',
         'profile.competences',
         'profile.driversLicenses',
         'profile.educations',
         'profile.jobFunctions',
         'profile.jobFunctions.jobFunctionCategory',
         'profile.jobFunctions.jobFunctionGroup',
         'profile.jobTypes',
         'profile.languages',
         'profile.statutes',
         'profilePicture',
         'recentPoke',
         'recentPoke.pokeType',
         'resume',
         'salutation',
         'socialSecurityNumbers',
         'socialSecurityNumbers.country',
         'status',
         'statuses.candidateStatusType',
         'submissions.journeyReferral',
         'tenant',
         'user',
         'videocallMeetings',
         'workExperiences',
         'workingDays',
      ],
      appends: ['locale', 'scopes', 'activation_state'],
   };

   constructor() {
      this.check(this.list, 'list');
      this.check(this.dropdown, 'dropdown');
      this.check(this.journey, 'journey');
   }

   check(includes: Record<'includes' | 'appends', Array<string>>, name: string): boolean {
      const includesDiff = includes.includes.filter((i) => !this.all.includes.includes(i));
      const appendsDiff = includes.appends.filter((i) => !this.all.appends.includes(i));

      if (includesDiff.length || appendsDiff.length) {
         console.group(`CANDIDATE_INCLUDES_CLASS: ${name}`);
         console.info('Missing includes:', includesDiff);
         console.info('Missing appends:', appendsDiff);
         console.groupEnd();
      }

      return !includesDiff.length && !appendsDiff.length;
   }
}

export const CANDIDATE_INCLUDES = new CANDIDATE_INCLUDES_CLASS();
// #endregion

export type CandidateTabTypes = 'profile' | 'activity' | 'journeys' | 'pools';

export class CandidateService {
   async getFilteredCandidates(query: LocationQuery, filters: Filters) {
      const response = await http.post<ApiResource<Array<Candidate>>>(`/candidates/search`, filters, {
         params: { ...query },
      });
      return response.data;
   }
   async getCandidatesDirect(query: LocationQuery) {
      const filters = {
         direct_search: {
            important: true,
            key: 'name',
            keyLabel: 'Zoeken',
            name: 'direct_search',
            value: query.search,
         },

         filter_elements: [],
         quick_search: [],
      } as Filters;

      delete query.search; // ensure search is only in filters and not query params

      const response = await http.post<ApiResource<Array<Candidate>>>(`/candidates/search`, filters, {
         params: {
            ...query,
            include: 'addresses.addressType,addresses.city',
         },
      });
      return response.data;
   }
   async getCandidates(query: LocationQuery) {
      const filters = {
         quick_search: [
            {
               important: true,
               key: 'name',
               keyLabel: 'Zoeken',
               name: 'search',
               value: query.search,
               valueLabel: `${query?.search} | Naam`,
            },
         ],
      } as Filters;

      const response = await http.post<ApiResource<Array<Candidate>>>(`/candidates/search`, filters, {
         params: { ...query },
      });
      return response.data;
   }
   async autocomplete(search: string) {
      const response = await http.get<ApiResource<Array<string>>>(`/candidates/autocomplete`, { params: { search, per_page: 25 } });
      return response.data;
   }
   async getCandidate(candidateId: number, query?: LocationQuery) {
      const response = await http.get<ApiResource<Candidate>>(`/candidates/${candidateId}`, { params: { ...query } });
      return response.data?.data;
   }
   async getPokes(candidateId: number) {
      const response = await http.get<ApiResource<Array<Poke>>>(`/candidates/${candidateId}/pokes`, {
         params: { page: 1, per_page: 9999 },
      });

      return response.data;
   }
   async postCandidate(candidate: Candidate) {
      const response = await http.post<ApiResource<Candidate>>(`/candidates`, candidate);
      return response.data?.data;
   }
   async putCandidate(candidateId: number, candidate: Candidate) {
      const response = await http.put<ApiResource<Candidate>>(`/candidates/${candidateId}`, candidate);
      return response.data?.data;
   }
   async deleteCandidate(candidateId: number, force: boolean) {
      const response = await http.delete(`/candidates/${candidateId}`, { params: { force } });
      return response;
   }
   async exportCandidates(query: LocationQuery, filters: Filters) {
      const response = await http.post('/candidates/export', filters, { params: { ...query } });
      return response.data;
   }
   async searchJourney(filters: Filters, query: LocationQuery) {
      const response = await http.post<ApiResource<Array<any>>>('/candidates/search-journey', filters, {
         params: { ...query },
      });
      return response.data;
   }
   async searchCandidateJourney(filters: Filters, query: LocationQuery) {
      const response = await http.post<ApiResource<Array<Candidate>>>('/candidates/search', filters, {
         params: { ...query },
      });
      return response.data;
   }
   async checkDelete(candidateId: number, force: boolean) {
      const response = await http.get<CandidateCheckDelete>(`/candidates/${candidateId}/check-delete`, {
         params: { force },
      });
      return response.data;
   }
}
